/* eslint-disable radix */
// @TODO: disable radix rule globally
import { intervalToDuration } from "date-fns";
import { leadZero } from "~/utils";

const EIGHT_HOURS_IN_MS = 8 * 60 * 60 * 1000;

const plur = (n: number, s: string) => (n !== 1 ? `${s}s` : s);

// we don't use date-fns because we want to have fastest possible solution
// and this functions might be called a lot
//
const formatStartSuggestion = (date: Date) => {
    const hours = date.getHours();
    const minutes = date.getMinutes();

    return `${leadZero(hours)}:${leadZero(minutes)}`;
};

const formatEndSuggestion = (date: Date, start: Date) => {
    const hours = date.getHours();
    const minutes = date.getMinutes();
    const startMinutes = start.getMinutes();
    const startHours = start.getHours();

    const hDiff = hours - startHours;
    const mDiff = minutes - startMinutes;
    const diff = hDiff + mDiff / 60;

    const diffStr =
        Math.floor(diff) > 0
            ? `${Number(diff.toFixed(1))} ${plur(diff, "hr")}`
            : `${diff * 60} mins`;

    return `${leadZero(hours)}:${leadZero(minutes)} (${diffStr})`;
};

export const getStartSuggestions = (date: Date) => {
    // each suggestion is 15 minutes
    // first suggestion is 08:00
    // last suggestion is 19:45

    const suggestions = [];
    const start = new Date(date);

    start.setHours(8, 0, 0, 0);

    for (let i = 0; i < 48; i++) {
        suggestions.push({
            label: formatStartSuggestion(start),
            date: new Date(start),
        });

        start.setMinutes(start.getMinutes() + 15);
    }

    return suggestions;
};

export const getEndSuggestions = (date: Date, start: Date) => {
    // 15, 30, 45, 60, then +30 (minutes)
    // first suggestion is based on start time
    // last suggestion should be less than 20:00

    const suggestions = [];
    const end = new Date(date);

    end.setHours(start.getHours(), start.getMinutes() + 15, 0, 0);

    let quarterCount = 0;

    while (
        end.getHours() < 20 &&
        end.getTime() - start.getTime() <= EIGHT_HOURS_IN_MS
    ) {
        suggestions.push({
            label: formatEndSuggestion(end, start),
            date: new Date(end),
        });

        if (quarterCount < 3) {
            quarterCount++;
            end.setMinutes(end.getMinutes() + 15);
        } else {
            end.setMinutes(end.getMinutes() + 30);
        }
    }

    return suggestions;
};

export const formatTime = (date: Date, round = true) => {
    let hours = 0;
    let minutes = 0;

    if (round) {
        minutes = Math.round(date.getMinutes() + date.getSeconds() / 60);
        hours = date.getHours() + Math.floor(minutes / 60);
        minutes %= 60;
    } else {
        hours = date.getHours();
        minutes = date.getMinutes();
    }

    return `${leadZero(hours)}:${leadZero(minutes)}`;
};

export const parseTimeString = (text: string) => {
    if (/[^\d\s:]/.test(text)) {
        return null;
    }

    const [hours, minutes, ...rest] = text.split(/[:\s]/);

    if (rest.length) {
        return null;
    }

    const hoursNumber = Number(hours || "0");
    const minutesNumber = Number(minutes || "0");

    if (hoursNumber > 23 || minutesNumber > 59) {
        return null;
    }

    return [hoursNumber, minutesNumber] as const;
};

export const isMinViolation = (
    parsed: ReturnType<typeof parseTimeString>,
    min: Date | null,
) => {
    if (!parsed || !min) {
        return false;
    }

    const [hours, minutes] = parsed;

    if (hours < min.getHours()) return true;
    if (hours > min.getHours()) return false;
    if (minutes < min.getMinutes()) return true;

    return false;
};

export const getFormattedDuration = (start: Date, end: Date) => {
    const duration = intervalToDuration({ start, end });
    const initialDays = Math.abs(duration.days ?? 0);
    const initialHours = Math.abs(duration.hours ?? 0);
    const initialMinutes = Math.abs(duration.minutes ?? 0);
    const initialSeconds = Math.abs(duration.seconds ?? 0);
    const neg = start.getTime() > end.getTime();

    let hours = initialHours + initialDays * 24;
    let minutes = Math.round(initialMinutes + initialSeconds / 60);

    if (minutes >= 60) {
        hours += Math.floor(minutes / 60);
        minutes %= 60;
    }

    return [hours, minutes, neg] as const;
};

export const formatDuration = (start: Date, end: Date) => {
    const [hours, minutes, neg] = getFormattedDuration(start, end);

    if (hours && minutes) {
        return `${neg ? "-" : ""}${hours}h ${minutes}m`;
    }

    if (hours) {
        return `${neg ? "-" : ""}${hours}h`;
    }

    if (minutes) {
        return `${neg ? "-" : ""}${minutes}m`;
    }

    return "0:00";
};

export const formatDurationFocused = (start: Date, end: Date) => {
    const [hours, minutes, neg] = getFormattedDuration(start, end);

    return `${neg ? "-" : ""}${hours}:${leadZero(minutes)}`;
};

export const parseDurationString = (text: string, neg?: boolean) => {
    if (
        (neg && /[^\d\s:hm-]/i.test(text)) ||
        (!neg && /[^\d\s:hm]/i.test(text))
    ) {
        return null;
    }

    const [n1, n2, ...rest] = text.trim().split(/[:\s]/);

    if (rest.length) {
        return null;
    }

    if (n1 && n2) {
        const hours = Number(n1.toLowerCase().replaceAll("h", ""));
        const minutes = Number(n2.toLowerCase().replaceAll("m", ""));

        if (isNaN(hours) || isNaN(minutes) || minutes > 59) {
            return null;
        }

        return [hours, minutes] as const;
    }

    const isHours = n1.toLowerCase().includes("h");

    if (isHours) {
        const hours = Number(n1.toLowerCase().replaceAll("h", ""));

        if (isNaN(hours)) {
            return null;
        }

        return [hours, 0] as const;
    }

    const finalText = n1.toLowerCase().replaceAll("m", "");

    if (finalText.length > 2) {
        const p1 = finalText.substring(0, finalText.length - 2);
        const p2 = finalText.substring(finalText.length - 2);
        const hours = Number(p1);
        const minutes = Number(p2);

        if (isNaN(hours) || isNaN(minutes) || minutes > 59) {
            return null;
        }

        return [hours, minutes] as const;
    }

    const v = Number(finalText);

    if (isNaN(v)) {
        return null;
    }

    if (v <= 24) {
        return [v, 0] as const;
    }

    return [0, v] as const;
};
